import {
  InlineAlertType,
  SupplementalDocumentRequest,
} from '@varo/graphql-schema-types';
import {
  PermissionStatus,
  requestCameraPermissionsAsync,
} from 'expo-image-picker';
import { useRouter } from 'expo-router';
import SocureDesktop from 'features/signup/ApplicationStatus/Review/Socure/SocureDesktop';
import React, { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { WebView } from 'react-native-webview';
import IconButton from 'shared/components/vxl/icon/IconButton/IconButton';
import InlineAlert from 'shared/components/vxl/InlineAlert/InlineAlert';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import { isMobileOS, isWeb } from 'shared/constants/platform';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import ActionScreen from 'shared/patterns/vxl/ActionScreen/ActionScreen';
import PermissionDisabledScreen from 'shared/patterns/vxl/Permission/PermissionDisabledScreen';
import { AuthContext, AuthContextProps } from 'shared/providers/auth';
import theme from 'shared/styles/theme';
import { webViewAdditionalProps } from 'shared/utils/webView/webViewAdditionalProps';

export interface SocureProps {
  socureDocRequest: SupplementalDocumentRequest;
  onSocureFlowCompleted: () => void;
  isReactivation: boolean;
  isReapplication?: boolean;
  onClose?: () => void;
}

const VARO_APP_DOMAIN = 'bank.varomoney.com';

const Socure = ({
  socureDocRequest,
  onSocureFlowCompleted,
  isReactivation = false,
  isReapplication = false,
  onClose,
}: SocureProps) => {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { t: translatedText } = useTranslation('signup', {
    keyPrefix: 'socure',
  });
  const [shouldStartExternalSocureFlow, setShouldStartExternalSocureFlow] =
    useState(false);
  const [isCameraPermissionDenied, setIsCameraPermissionDenied] =
    useState(false);
  const socureDocRequestUrl = socureDocRequest?.url || '';

  const handleSocureFlowInitiatedAnalytics = usePressableAnalytics({
    analyticsId: 'socureFlowInitiated',
    customAttributes: {
      vendor: 'Socure',
      ...(isReactivation && { reactivation_flow: 'true' }),
      ...(isReapplication && { reapplication_flow: 'true' }),
    },
  });

  useViewDidFocusScreenAnalyticsEvent({
    key: 'socureFlowScreen',
    customAttributes: {
      ...(isReactivation && { reactivation_flow: 'true' }),
      ...(isReapplication && { reapplication_flow: 'true' }),
    },
  });

  const { handleLogout } = useContext<AuthContextProps>(AuthContext);

  const handleBackButtonAction = useCallback(() => {
    if (isReactivation) {
      router.replace('/reactivation');
    } else {
      router.replace('/login');
    }
  }, [isReactivation, router]);

  useBackHandler(handleBackButtonAction);

  // Show only for desktop web, mobileOS web will use iFrame rendered below
  if (isWeb && !isMobileOS) {
    return (
      <SocureDesktop
        socureDocRequest={socureDocRequest}
        onSocureFlowCompleted={onSocureFlowCompleted}
        isReactivation={isReactivation}
      />
    );
  }

  const BUTTON_HEIGHT = 60;
  const BUTTON_SPACING = theme.spacing.m;

  const footerHeightEstimate =
    BUTTON_HEIGHT * 2 + BUTTON_SPACING * 2 + insets.bottom;

  if (isCameraPermissionDenied) {
    return (
      <PermissionDisabledScreen
        titleText={translatedText('cameraPermissionDisabled.heading')}
        contentList={[
          translatedText('cameraPermissionDisabled.bullets.one'),
          translatedText('cameraPermissionDisabled.bullets.two'),
        ]}
        onClose={() => setIsCameraPermissionDenied(false)}
        primaryButtonText={translatedText(
          'cameraPermissionDisabled.buttons.openSettings',
        )}
        visual="TOGGLE_A"
        visualAccessibilityLabel="Toggle button image"
      />
    );
  }

  if (!shouldStartExternalSocureFlow) {
    return (
      <>
        <ActionScreen
          titleText={translatedText('interstitial.title')}
          titleTextStyle="extra-large"
          bodyContent={
            <>
              <BodyBaseText numberOfLines={3} style={styles.subtitle}>
                {translatedText('interstitial.subtitle')}
              </BodyBaseText>

              <BodyBaseText numberOfLines={3}>
                {translatedText('interstitial.description')}
              </BodyBaseText>
            </>
          }
          primaryButton={{
            label: translatedText('interstitial.button'),
            onPress: async () => {
              handleSocureFlowInitiatedAnalytics();

              if (Platform.OS === 'ios') {
                try {
                  const { status } = await requestCameraPermissionsAsync();
                  if (status === PermissionStatus.DENIED) {
                    setIsCameraPermissionDenied(true);
                    return;
                  }
                } catch {
                  setIsCameraPermissionDenied(true);
                  return;
                }
              }

              setShouldStartExternalSocureFlow(true);
            },
          }}
          secondaryButton={{
            label: !isReactivation
              ? translatedText('interstitial.logOut')
              : translatedText('interstitial.viewPreviousAccountInfo'),
            onPress: () => {
              if (isReactivation) {
                router.push('/reactivation');
              } else {
                void handleLogout();
              }
            },
          }}
          visual="ID_A"
          topPadding={60}
        />
        <View
          style={[
            styles.inlineAlertContainer,
            { bottom: footerHeightEstimate },
          ]}
        >
          <InlineAlert
            alertType={InlineAlertType.Info}
            bodyText={translatedText('interstitial.infoMessage')}
            iconKeyType="LOCK"
          />
        </View>
      </>
    );
  }

  if (isWeb) {
    // redirect to socure URL if mobile OS web and shouldStartExternalSocureFlow is true
    global.location.href = socureDocRequestUrl;
    return null;
  }

  return (
    <View style={[styles.container, { paddingTop: insets.top }]}>
      <View style={styles.closeButtonContainer}>
        <IconButton
          iconButtonAccessibilityLabel="CLOSE"
          iconKey="CLOSE"
          iconOnPress={() => {
            setShouldStartExternalSocureFlow(false);
            onClose?.();
          }}
        />
      </View>
      <WebView
        {...webViewAdditionalProps}
        testID="socureIdvWebView"
        style={styles.webview}
        source={{ uri: socureDocRequestUrl }}
        allowsInlineMediaPlayback
        mediaPlaybackRequiresUserAction={false}
        mediaCapturePermissionGrantType="grant"
        onNavigationStateChange={(navState) => {
          if (navState.url.includes(VARO_APP_DOMAIN)) {
            setShouldStartExternalSocureFlow(false);
            onSocureFlowCompleted();
          }
        }}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  closeButtonContainer: {
    padding: theme.spacing.m,
  },
  container: {
    backgroundColor: '#F6F6F6', // Socure BG color so not a theme variable
    display: 'flex',
    height: '100%',
  },
  inlineAlertContainer: {
    left: 0,
    marginHorizontal: theme.spacing.m,
    position: 'absolute',
    right: 0,
    zIndex: 10,
  },
  subtitle: {
    marginBottom: theme.spacing.m,
  },
  webview: {
    flex: 1,
  },
});

export default Socure;
